import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AccountingSystemSearchForm.module.scss';
import { AGING_BUCKETS, isAgingBucketKey } from './AgingOverview/agingSummary.helper';
import useAgingBucketLabels from './useAgingBucketLabels';

import { Button } from '#shared/components/UI';

const { Box, Select, TextField, DateRangePicker, Radio } = vp;

export type Currency = 'all' | 'CAD' | 'USD' | '';
export type StatusFilter = 'all' | 'partiallyPaid' | 'reversed' | 'open' | '';
// The aging bucket the open list is narrowed to (the summary card's tile or
// the filter bar's aging select — same filter either way).
export type AgingBucketFilter = vp.AspAgingBucketKey | '';
// Which date the closed tab filters and sorts on: when the item was settled
// (payment / received date) or when it was created (bill / invoice date).
export type DateFilterType = 'documentDate' | 'closedDate';
export type TimeRange =
  | 'allTime'
  | 'custom'
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'last6Months'
  | 'last12Months';

export type SearchParams = {
  currency: Currency;
  searchTerm: string;
  timeRange: TimeRange;
  startDate: vp.Timestamp | undefined;
  endDate: vp.Timestamp | undefined;
  status?: StatusFilter;
  dateFilterType?: DateFilterType;
  agingBucket?: AgingBucketFilter;
};

type SearchFormProps = {
  currency?: Currency;
  searchTerm?: string;
  timeRange?: TimeRange;
  startDate?: vp.Timestamp | undefined;
  endDate?: vp.Timestamp | undefined;
  status?: StatusFilter;
  dateFilterType?: DateFilterType;
  agingBucket?: AgingBucketFilter;
  activeTab?: 'open' | 'closed';
  country: string;
  section: 'Payable' | 'Receivable';
  // Draws the focus treatment around the custom date range after an aging
  // tile applied it, so the user sees where the filter landed.
  isDateRangeHighlighted?: boolean;
  // Same treatment on the currency select when the tile also applied its
  // currency (multi-currency accounts only).
  isCurrencyHighlighted?: boolean;
  onSearch: (params: SearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const AccountingSystemSearchForm: FC = (props: SearchFormProps) => {
  const {
    currency: currencyFromProps = '',
    searchTerm: searchTermFromProps = '',
    timeRange: timeRangeFromProps = 'custom',
    startDate: startDateFromProps,
    endDate: endDateFromProps,
    status: statusFromProps = '',
    dateFilterType: dateFilterTypeFromProps = 'closedDate',
    agingBucket: agingBucketFromProps = '',
    activeTab = 'open',
    isDateRangeHighlighted = false,
    isCurrencyHighlighted = false,
    onSearch,
    section,
  } = props;

  // Internal state
  const [currency, setCurrency] = useState(currencyFromProps);
  const [searchTerm, setSearchTerm] = useState(searchTermFromProps);
  const [timeRange, setTimeRange] = useState(timeRangeFromProps);
  const [startDate, setStartDate] = useState(startDateFromProps);
  const [endDate, setEndDate] = useState(endDateFromProps);
  const [status, setStatus] = useState(statusFromProps);
  const [dateFilterType, setDateFilterType] = useState(dateFilterTypeFromProps);
  const [agingBucket, setAgingBucket] = useState(agingBucketFromProps);

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const agingBucketLabels = useAgingBucketLabels();

  // Update internal state when props change (for external reset)
  useEffect(() => {
    setCurrency(currencyFromProps);
  }, [currencyFromProps]);

  useEffect(() => {
    setSearchTerm(searchTermFromProps);
  }, [searchTermFromProps]);

  useEffect(() => {
    setTimeRange(timeRangeFromProps);
  }, [timeRangeFromProps]);

  useEffect(() => {
    setStartDate(startDateFromProps);
  }, [startDateFromProps]);

  useEffect(() => {
    setEndDate(endDateFromProps);
  }, [endDateFromProps]);

  useEffect(() => {
    setStatus(statusFromProps);
  }, [statusFromProps]);

  useEffect(() => {
    setDateFilterType(dateFilterTypeFromProps);
  }, [dateFilterTypeFromProps]);

  useEffect(() => {
    setAgingBucket(agingBucketFromProps);
  }, [agingBucketFromProps]);

  const TimeRanges = [
    { value: 'allTime', label: t('TimeRanges.allTime', { defaultValue: 'All Time' }) },
    { value: 'custom', label: t('TimeRanges.custom', { defaultValue: 'Custom Date' }) },
    { value: 'today', label: t('TimeRanges.today', { defaultValue: 'Today' }) },
    { value: 'yesterday', label: t('TimeRanges.yesterday', { defaultValue: 'Yesterday' }) },
    { value: 'thisWeek', label: t('TimeRanges.thisWeek', { defaultValue: 'This week' }) },
    { value: 'lastWeek', label: t('TimeRanges.lastWeek', { defaultValue: 'Last week' }) },
    { value: 'thisMonth', label: t('TimeRanges.thisMonth', { defaultValue: 'This month' }) },
    { value: 'lastMonth', label: t('TimeRanges.lastMonth', { defaultValue: 'Last month' }) },
    { value: 'thisQuarter', label: t('TimeRanges.thisQuarter', { defaultValue: 'This quarter' }) },
    { value: 'lastQuarter', label: t('TimeRanges.lastQuarter', { defaultValue: 'Last quarter' }) },
    { value: 'last6Months', label: t('TimeRanges.last6Months', { defaultValue: 'Last 6 months' }) },
    { value: 'last12Months', label: t('TimeRanges.last12Months', { defaultValue: 'Last 12 months' }) },
  ];

  const currencyOptions = [
    { value: 'all', label: t('Currency.Options.all', { defaultValue: 'All' }) },
    { value: 'CAD', label: 'CAD' },
    { value: 'USD', label: 'USD' },
  ];

  const statusOptions = [
    { value: 'all', label: t('Status.Options.all', { defaultValue: 'All' }) },
    {
      value: 'partiallyPaid',
      label:
        section === 'Receivable'
          ? t('Status.Options.partiallyCollected', { defaultValue: 'Partially Collected' })
          : t('Status.Options.partiallyPaid', { defaultValue: 'Partially Paid' }),
    },
    ...(section === 'Payable'
      ? [{ value: 'reversed', label: t('Status.Options.reversed', { defaultValue: 'Reversed' }) }]
      : []),
    { value: 'open', label: t('Status.Options.open', { defaultValue: 'Open' }) },
  ];

  const agingBucketOptions = AGING_BUCKETS.map((bucket) => ({ value: bucket, label: agingBucketLabels[bucket] }));

  const closedDateLabel =
    section === 'Receivable'
      ? t('DateFilterType.Options.receivedDate', { defaultValue: 'Received Date' })
      : t('DateFilterType.Options.paymentDate', { defaultValue: 'Payment Date' });

  const documentDateLabel =
    section === 'Receivable'
      ? t('DateFilterType.Options.invoiceDate', { defaultValue: 'Invoice Date' })
      : t('DateFilterType.Options.billDate', { defaultValue: 'Bill Date' });

  const dateTypeLabel = dateFilterType === 'documentDate' ? documentDateLabel : closedDateLabel;

  return (
    <Box jc="sb" flx="col" gap="md">
      <Box gap="sm" wrap="wrap">
        <Box
          width={activeTab === 'closed' ? '290px' : '175px'}
          className={isDateRangeHighlighted ? styles.fieldHighlight : undefined}
        >
          <Select
            version="2"
            placeholder={
              section === 'Receivable'
                ? t('dateReceivablePlaceholder', { defaultValue: 'Invoice Date Period' })
                : t('datePlaceholder', { defaultValue: 'Bill Date Period' })
            }
            options={TimeRanges}
            name="date"
            value={timeRange}
            onBlur={() => {}}
            onChange={(e) => {
              setTimeRange(e.target.value as TimeRange);
            }}
            renderSelectedValue={
              activeTab === 'closed'
                ? (option, { label, Placeholder }) =>
                    option ? (
                      t('rangeByDateType', {
                        defaultValue: '{{range}} by {{dateType}}',
                        range: label,
                        dateType: dateTypeLabel,
                      })
                    ) : (
                      <Placeholder />
                    )
                : undefined
            }
            renderDropdownHeader={
              activeTab === 'closed'
                ? () => (
                    <div className={styles.dateTypeHeader}>
                      <div className={styles.dateTypeLabel}>
                        {section === 'Receivable'
                          ? t('DateFilterType.headerReceivable', { defaultValue: 'Filter Invoices Based On' })
                          : t('DateFilterType.header', { defaultValue: 'Filter Bills Based On' })}
                      </div>

                      <Radio
                        variant="outlineShadow"
                        name="dateFilterType"
                        noOrdering
                        value={dateFilterType}
                        options={{ closedDate: closedDateLabel, documentDate: documentDateLabel }}
                        onChange={(e) => {
                          setDateFilterType(e.target.value as DateFilterType);
                        }}
                        onBlur={() => {}}
                      />
                    </div>
                  )
                : undefined
            }
          />
        </Box>

        {timeRange === 'custom' && (
          <div data-testid="date-range-wrapper" data-highlighted={isDateRangeHighlighted || undefined}>
            {/* Keyed on the applied range: the picker's nested internal state
                does not reliably take externally-applied values (e.g. an aging
                tile setting the range), so a range change re-seeds it. */}
            <DateRangePicker
              key={`${String(startDate)}-${String(endDate)}`}
              initialTime={startDate && endDate ? [startDate, endDate] : undefined}
              onChange={(range) => {
                setStartDate(range[0]);
                setEndDate(range[1]);
              }}
              allowBlank
              value={startDate && endDate ? [startDate, endDate] : null}
              triggerVariant="outlineShadow"
              isHighlighted={isDateRangeHighlighted}
            />
          </div>
        )}

        <div
          className={[styles.currencyField, isCurrencyHighlighted ? styles.fieldHighlight : ''].join(' ')}
          data-testid="currency-filter-wrapper"
          data-highlighted={isCurrencyHighlighted || undefined}
        >
          <Select
            version="2"
            placeholder={t('Currency.placeholder', { defaultValue: 'Currency' })}
            options={currencyOptions}
            name="currency"
            value={currency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrency(e.target.value as Currency);
            }}
          />
        </div>

        {activeTab === 'open' && (
          <Box width="175px">
            <Select
              version="2"
              placeholder={t('Status.placeholder', { defaultValue: 'Status' })}
              options={statusOptions}
              name="status"
              value={status}
              onBlur={() => {}}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
              }}
            />
          </Box>
        )}

        {activeTab === 'open' && (
          <Box width="175px">
            {/* The chip the summary card's tile selection shows up as, and a
                second way to pick a bucket; the × clears it (Figma "Aging
                filter"). */}
            <Select
              version="2"
              placeholder={t('AgingBucket.placeholder', { defaultValue: 'Aging' })}
              options={agingBucketOptions}
              name="agingBucket"
              value={agingBucket}
              showClear
              onBlur={() => {}}
              onChange={(e) => {
                setAgingBucket(isAgingBucketKey(e.target.value) ? e.target.value : '');
              }}
            />
          </Box>
        )}

        <TextField
          key="AccountingSoftwareKeyword"
          name="AccountingSoftwareKeyword"
          placeholder={t('KeywordPlaceholder', { defaultValue: 'Keyword' })}
          value={searchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />

        <Button
          id="search-accounting-software"
          size="small"
          colorScheme="primary"
          // Only the custom range needs both dates picked; the preset ranges
          // (including the unbounded 'allTime') are resolved by the container,
          // so they must stay searchable with no dates in state.
          disabled={
            timeRange === 'custom' &&
            (startDate === undefined || endDate === undefined) &&
            agingBucket === agingBucketFromProps
          }
          onClick={() => {
            onSearch({
              currency,
              searchTerm,
              timeRange,
              startDate,
              endDate,
              status,
              dateFilterType,
              agingBucket,
            });
          }}
        >
          {tGlobal('Buttons.search', { defaultValue: 'Search' })}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountingSystemSearchForm;
